/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useGetBootstrapAppPage } from '../useGetAppPage'
import { useSessionStorage } from '@/hooks'
import { ENDPOINTS } from '@/util'
import { JsonFormSchemaV2 } from '../../component-library/FormComponent/types'
import { getPageFilterIds, joinNewAndPersistedFilters } from '../utils'
import { FilterResponse } from './types'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { isJsonFormSchema } from '../../component-library/StatementEditorComponent/types-v6'
// import { json } from '../../../mock-data/filters/page-filters-response-2.json'

interface GetFilters {
  appKey?: string
  filterIds?: string[]
  projectId?: number
  pageFilterStorageKey?: string
}

function buildFilterResultAndPersist(data: FilterResponse, pageFilterStorageKey: string) {
  const filtersData = isJsonFormSchema(data.uiSchema) ? data.uiSchema : { type: 'object' as const, properties: [] }
  const defaultSelections = isJsonFormSchema(filtersData)
    ? filtersData.properties.map((property) => {
        return {
          [property.id]: property.default
            ? property.default
            : property.type === 'boolean'
              ? false
              : property.type === 'cascading'
                ? {}
                : property.type === 'array'
                  ? []
                  : '',
        }
      })
    : []
  const filterSelectionsPersistedJson = sessionStorage.getItem(pageFilterStorageKey)
  const filtersSelectionsPersisted = filterSelectionsPersistedJson ? JSON.parse(filterSelectionsPersistedJson) : null
  const persistSelections = (selections: Record<string, any>[]) => {
    sessionStorage.setItem(pageFilterStorageKey, JSON.stringify(selections))
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: pageFilterStorageKey,
        newValue: JSON.stringify(selections),
        storageArea: sessionStorage,
      })
    )
  }

  if (!filtersSelectionsPersisted) {
    persistSelections(defaultSelections)
  } else {
    const allFiltersWithSelections = joinNewAndPersistedFilters(filtersSelectionsPersisted, defaultSelections)
    persistSelections(allFiltersWithSelections)
  }
  return { filtersData, defaultSelections }
}

const getPageFilters = async ({ appKey, filterIds, projectId, pageFilterStorageKey }: GetFilters) => {
  if (!appKey || !filterIds || !projectId || !pageFilterStorageKey) {
    throw new Error('Missing required parameters')
  }
  // Short-circuit when there are no filter ids: return empty uiSchema/filters
  if (filterIds.length === 0) {
    const data: FilterResponse = { uiSchema: {}, filters: {} as any }
    return buildFilterResultAndPersist(data, pageFilterStorageKey)
  }
  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) {
    throw new Error('No token found')
  }
  const url = `${ENDPOINTS.CC_DOTNET}/content`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      app_id: appKey,
      content_id: `/${appKey}/all_filters`,
      project_id: projectId,
      filters: [{ ids: filterIds }],
    }),
  })
  const data = (await response.json()) as FilterResponse
  if (response.ok) {
    return buildFilterResultAndPersist(data, pageFilterStorageKey)
  }
  throw new Error('Failed to fetch app page')
}

export function useGetPageFilters() {
  const { appKey, projectId, pageFilterStorageKey } = useAppContext()
  const {
    data: { pageFilters },
  } = useGetBootstrapAppPage()
  const [filtersSelectionsPersisted] = useSessionStorage<Record<string, any>[] | ''>(pageFilterStorageKey, '')

  const pageFilterIds = useMemo(() => getPageFilterIds(pageFilters), [pageFilters])
  const allFiltersContentId = useMemo(() => (appKey ? `/${appKey}/all_filters` : ''), [appKey])

  const selectFilterData = useCallback(
    (data: { filtersData: JsonFormSchemaV2; defaultSelections: Record<string, any>[] }) => {
      return {
        type: 'filter',
        storageKey: pageFilterStorageKey,
        data:
          filtersSelectionsPersisted && typeof filtersSelectionsPersisted !== 'string'
            ? updateSchemaWithDefaults(data.filtersData, filtersSelectionsPersisted)
            : data.filtersData,
        defaultSelections: data.defaultSelections,
      }
    },
    [pageFilterStorageKey, filtersSelectionsPersisted]
  )

  return useSuspenseQuery({
    queryKey: [allFiltersContentId, ...pageFilterIds, appKey, projectId],
    queryFn: () =>
      getPageFilters({
        appKey,
        filterIds: pageFilterIds,
        projectId,
        pageFilterStorageKey,
      }),
    select: selectFilterData,
  })
}

export function updateSchemaWithDefaults(
  schema: JsonFormSchemaV2,

  defaultsArray: Array<Record<string, any>>
): JsonFormSchemaV2 {
  // Create a deep copy of the schema to avoid mutations
  const updatedSchema: JsonFormSchemaV2 = JSON.parse(JSON.stringify(schema))

  // Create a map of field names to default values for easier lookup

  const defaultsMap: Record<string, any> = {}
  defaultsArray.forEach((defaultObj) => {
    const key = Object.keys(defaultObj)[0]
    defaultsMap[key] = defaultObj[key]
  })

  updatedSchema.properties.forEach((property) => {
    // Since property keys are exactly the same as storage keys, use direct lookup
    if (defaultsMap[property.id] !== undefined) {
      const propertyIndex = updatedSchema.properties.findIndex((p) => p.id === property.id)
      if (propertyIndex !== -1) {
        updatedSchema.properties[propertyIndex].default = defaultsMap[property.id]
      }
    }
  })

  return updatedSchema
}
